import type { DragEvent } from 'react';
import { useCallback, useState } from 'react';

import { useNavigate } from '@tanstack/react-router';
import type { FileRejection } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';

import { useDragState } from 'src/apps/myFiles/app/providers/DragStateProvider';

import { DropAccept } from './DropAccept';
import { DropInfo } from './DropInfo';
import { DropReject } from './DropReject';

import { useLingui } from '@lingui/react/macro';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import { useHasSubscription } from 'src/api/queries';
import {
  dropZoneAcceptedContentTypes,
  trackImportAttempted,
} from 'src/utils/importHelpers';

export const DropZone = () => {
  const { t } = useLingui();
  const dragState = useDragState();
  const navigate = useNavigate();

  const hasSubscription = useHasSubscription();

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      dragState.dispatch({
        type: 'upload_files',
        payload: {
          acceptFiles: acceptedFiles,
          rejectFiles: rejectedFiles,
        },
      });
      trackImportAttempted({
        componentLocation: ComponentLocations.FRONT_PAGE.DROP_ZONE,
        acceptedFiles,
        rejectedFiles,
      });
      void navigate({ to: '/myfiles/$' });
    },
    [dragState, navigate]
  );

  const [numberOfFiles, setNumberOfFiles] = useState(0);
  const onDragEnter = (e: DragEvent<HTMLDivElement>) => {
    setNumberOfFiles(e.dataTransfer.items.length);
  };

  const acceptedContentTypes = dropZoneAcceptedContentTypes(
    hasSubscription.data
  );

  const {
    getRootProps,
    getInputProps,
    open,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    onDragEnter,
    accept: acceptedContentTypes,
    noClick: true,
    maxSize: 104857600,
  });

  return (
    // react-dropzone sets tabIndex=0 on the root by default, but we disable it to avoid duplicate focusable elements.
    <div data-cy="dropzone" {...getRootProps()} tabIndex={-1}>
      <input
        aria-label={t`Upload files by dragging and dropping`}
        data-cy="dropzone-input"
        {...getInputProps()}
      />
      {/* Safari issue is causing isDragAccept and isDragReject to always be false */}
      {(isDragAccept || (isDragActive && !isDragAccept && !isDragReject)) && (
        <DropAccept />
      )}
      {isDragReject && <DropReject numberOfFiles={numberOfFiles} />}
      {!isDragActive && <DropInfo importFiles={open} />}
    </div>
  );
};
